import React, { Suspense, lazy, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './App.css';
import ClientSidebar from './components/sidebar/ClientSidebar';
import ChatInterface from './components/chat/ChatInterface';
import CumulativeTopicsPopover from './components/chat/CumulativeTopicsPopover';
import ModeSelector from './components/chat/ModeSelector';
import ReportPanel from './components/report/ReportPanel';
import ReportHamburgerMenu from './components/report/ReportHamburgerMenu';
import ReportTurnNavigator from './components/report/ReportTurnNavigator';
import CreditsBadge from './components/report/CreditsBadge';
import useIsMobileViewport from './hooks/useIsMobileViewport';
import useMediaQuery from './hooks/useMediaQuery';
import useRightSwipe from './hooks/useRightSwipe';
import ConfirmDialog from './components/dialogs/ConfirmDialog';
import NotificationDialog from './components/dialogs/NotificationDialog';
import {
  downloadAccountFullSessionReport,
  downloadAccountFullSessionTranscript,
  getAccountSpecificSkillReplay,
  getAccountSpecificStrategyReplay,
  getAccountFullSessionReplay,
  getUserPreferences,
  resetSession,
  updateUserPreferences,
} from './services/api';
import { normalizeUserAvatarPreference, UserAvatarIcon } from './components/chat/ChatAvatarIcons';
import { useAuth } from './AuthWrapper';
import useClientProfiles, { getMetaKeyForPersona } from './hooks/useClientProfiles';
import useOarsFlow from './hooks/useOarsFlow';
import useSpecificStrategiesFlow from './hooks/useSpecificStrategiesFlow';
import useAppViewState from './hooks/useAppViewState';
import useSessionTransition, { INITIAL_REVEALED_ITEMS, INITIAL_STAGE_METRICS } from './hooks/useSessionTransition';
import useFeedbackWorkflow from './hooks/useFeedbackWorkflow';
import useContactOverlay from './hooks/useContactOverlay';
import useAppShell from './hooks/useAppShell';
import useNotificationSystem from './hooks/useNotificationSystem';
import { uoftLogoUrl } from './assets/assetPaths';
import { LanguageContext, translate } from './LanguageContext';
import enTranslations from './locales/en.json';
import viTranslations from './locales/vi.json';
import frTranslations from './locales/fr.json';
import esTranslations from './locales/es.json';
import zhTranslations from './locales/zh.json';
import arTranslations from './locales/ar.json';
import { getCounsellorOverlayFromMode } from './components/overlays/counsellorOverlayConfig';
import GuidedHighlightHoverTooltip from './components/overlays/GuidedHighlightHoverTooltip';

const LOCALE_MAP = { en: enTranslations, vi: viTranslations, fr: frTranslations, es: esTranslations, zh: zhTranslations, ar: arTranslations };
const SINGLE_COLUMN_VIEWPORT_QUERY = '(max-width: 816px)';
// Keep in sync with the drawer slide animation in App.css.
const SINGLE_COLUMN_DRAWER_ANIMATION_MS = 260;
const COMPACT_MODE_NAVIGATOR_QUERY = '(min-width: 817px) and (max-width: 1067px)';
const COMPACT_SESSION_CONTROLS_QUERY = '(max-width: 1067px)';

// Bring an element to the top of its own scroll container without ever
// touching the document scroll position. scrollIntoView walks all the way up
// and scrolls the document too, which on Chrome for iOS snaps the page up by
// the browser-UI inset instead of landing at the visual top.
function scrollAncestorToTop(target) {
  let node = target.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      node.scrollTop += target.getBoundingClientRect().top - node.getBoundingClientRect().top;
      return;
    }
    node = node.parentElement;
  }
}
const CustomClientBuilder = lazy(() => import('./components/custom-client-builder/CustomClientBuilder'));
const OverlayRouter = lazy(() => import('./components/overlays/OverlayRouter'));
const ExpandedSectionModal = lazy(() => import('./components/dialogs/ExpandedSectionModal'));
const FeedbackDialog = lazy(() => import('./components/dialogs/FeedbackDialog'));

const clientCodingRows = [
  ['Desire+', 'Change Talk', 'Client expresses desire to change (e.g., "I want to quit")'],
  ['Ability+', 'Change Talk', 'Client expresses optimism about ability to change'],
  ['Reason+', 'Change Talk', 'Client provides reasons for changing'],
  ['Need+', 'Change Talk', 'Client expresses need to change'],
  ['Commitment+', 'Change Talk', 'Client expresses commitment to change'],
  ['Activation+', 'Change Talk', 'Client leans toward action or suggests alternatives'],
  ['TakingSteps+', 'Change Talk', 'Client mentions recent steps toward change'],
  ['Other+', 'Change Talk', 'Supports change but does not fit other categories'],
  ['Desire-', 'Sustain Talk', 'Client expresses desire to maintain behavior'],
  ['Ability-', 'Sustain Talk', 'Client expresses pessimism about ability to change'],
  ['Reason-', 'Sustain Talk', 'Client provides reasons for maintaining behavior'],
  ['Need-', 'Sustain Talk', 'Client expresses need to maintain behavior'],
  ['Commitment-', 'Sustain Talk', 'Client expresses commitment to maintain behavior'],
  ['Activation-', 'Sustain Talk', 'Client leans toward inaction'],
  ['TakingSteps-', 'Sustain Talk', 'Client mentions steps reinforcing behavior'],
  ['Other-', 'Sustain Talk', 'Supports maintaining behavior but does not fit other categories'],
  ['Neutral', 'Neutral', 'Statement that neither supports nor opposes change'],
];

const counsellorCodingRows = [
  ['AF', 'MI-Adherent', 'Affirm - Supportive statement about client strengths'],
  ['SR', 'MI-Adherent', 'Simple Reflection - Rephrasing client content'],
  ['CR', 'MI-Adherent', 'Complex Reflection - Reflection adding meaning'],
  ['Seek', 'MI-Adherent', 'Seek Collaboration - Inviting client input'],
  ['Emphasize', 'MI-Adherent', 'Emphasize Autonomy - Supporting client choice'],
  ['Persuade with', 'MI-Adherent', 'Persuade with Permission - Advice with permission'],
  ['Persuade', 'MI-Inconsistent', 'Persuade - Advice without permission'],
  ['Confront', 'MI-Inconsistent', 'Confront - Disagreeing, arguing, or shaming'],
  ['GI', 'Other', 'Giving Information - Neutral information sharing'],
  ['Q', 'Other', 'Question - Asking for information from client'],
  ['NC', 'Other', 'Non-Codable - Utterances that cannot be classified'],
];

function buildCompletedSessionMessages(sessionLabel, actionLine) {
  return {
    completedMessage:
      `${sessionLabel} is complete.\n\n` +
      `${actionLine}\n\n` +
      'Do you want to continue?',
    completedUndownloadedFeedbackMessage:
      'You have not downloaded your feedback report yet.\n\n' +
      'It contains more detailed coaching and session analysis than what is shown here.\n\n' +
      `${actionLine}\n\n` +
      'Do you want to continue?',
  };
}

function saveBlobAsDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function openBlobDocument(blob, filename, pendingTab = null) {
  const url = window.URL.createObjectURL(blob);
  if (pendingTab && !pendingTab.closed) {
    pendingTab.location.href = url;
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened) {
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  window.URL.revokeObjectURL(url);
  saveBlobAsDownload(blob, filename);
}

function App() {
  const { authenticated, guest, requireAuth, signOut } = useAuth();
  const isMobileViewport = useIsMobileViewport();
  const isSingleColumnViewport = useMediaQuery(SINGLE_COLUMN_VIEWPORT_QUERY);
  const compactModeNavigator = useMediaQuery(COMPACT_MODE_NAVIGATOR_QUERY);
  const compactSessionControls = useMediaQuery(COMPACT_SESSION_CONTROLS_QUERY);
  const [activeShellPanel, setActiveShellPanel] = useState('chat');
  const [singleColumnMenuOpen, setSingleColumnMenuOpen] = useState(false);
  // Lags singleColumnMenuOpen on close so the drawer can slide out before it
  // unmounts. Every close path flips singleColumnMenuOpen, so this covers them all.
  const [singleColumnDrawerMounted, setSingleColumnDrawerMounted] = useState(false);
  const singleColumnMenuRef = useRef(null);
  const [drawerAccountMenuOpen, setDrawerAccountMenuOpen] = useState(false);
  const drawerAccountMenuRef = useRef(null);
  const drawerAccountMenuPortalRef = useRef(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionReportMode, setSessionReportMode] = useState(null);
  const [sessionCounsellorMode, setSessionCounsellorMode] = useState(null);
  const [fullSessionVignette, setFullSessionVignette] = useState(null);
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const [conversationStarted, setConversationStarted] = useState(false);
  const [systemMessagesHistory, setSystemMessagesHistory] = useState([]); // Array of {counsellor: [], client: []} per turn
  const [fullSessionGuidanceVisible, setFullSessionGuidanceVisible] = useState(false);
  const [viewedTurnIndex, setViewedTurnIndex] = useState(null); // null = viewing latest turn
  const [highlightedTurnIndex, setHighlightedTurnIndex] = useState(null); // turn hovered in the TS3 tree
  const [revealedItems, setRevealedItems] = useState(INITIAL_REVEALED_ITEMS);
  const [topicsPanelOpen, setTopicsPanelOpen] = useState(false);
  const [unreadTopicCount, setUnreadTopicCount] = useState(0);
  const trackedTopicsRef = useRef({ scope: null, keys: new Set() });
  const [stageMetrics, setStageMetrics] = useState(INITIAL_STAGE_METRICS);
  const [streamingActive, setStreamingActive] = useState(false);
  const [postDownloadNextStepCue, setPostDownloadNextStepCue] = useState(false);
  const [feedbackPdfDownloaded, setFeedbackPdfDownloaded] = useState(false);
  const [completedFullSessionViewActive, setCompletedFullSessionViewActive] = useState(false);
  const [compactPostSessionStatus, setCompactPostSessionStatus] = useState(null);
  const [compactSessionObserverStatus, setCompactSessionObserverStatus] = useState(null);
  const [compactTurnNavigatorStatus, setCompactTurnNavigatorStatus] = useState(null);
  const [guidedHighlightsEnabled, setGuidedHighlightsEnabled] = useState(() => {
    const stored = localStorage.getItem('guided_highlights_enabled');
    return stored === null ? true : stored === 'true';
  });
  const [skillsAnnotationsEnabled, setSkillsAnnotationsEnabled] = useState(() => {
    const stored = localStorage.getItem('skills_annotations_enabled');
    return stored === null ? true : stored === 'true';
  });
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(() => {
    const stored = localStorage.getItem('suggestions_enabled');
    return stored === null ? true : stored === 'true';
  });
  const [preferredTheme, setPreferredTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [preferredAnalyticsVisible, setPreferredAnalyticsVisible] = useState(() => {
    const stored = localStorage.getItem('analytics_visible');
    return stored === null ? true : stored === 'true';
  });
  const [userAvatarPreference, setUserAvatarPreference] = useState(() => normalizeUserAvatarPreference());
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [customProfile, setCustomProfile] = useState(null);
  const [clientModeCounsellorMode, setClientModeCounsellorMode] = useState(null);
  const [btcActivationRequest, setBtcActivationRequest] = useState(0);
  const [btcCueFlowActive, setBtcCueFlowActive] = useState(false);
  const [btcPostSessionNextStepCue, setBtcPostSessionNextStepCue] = useState(false);
  const [chatKey, setChatKey] = useState(0);
  const [historicalFullSessionReplay, setHistoricalFullSessionReplay] = useState(null);

  const topicKeys = useMemo(() => {
    const keys = [];
    ['motivations', 'barriers', 'next_steps'].forEach((category) => {
      (revealedItems?.[category] || []).forEach((item) => keys.push(`${category}:${item}`));
    });
    return keys;
  }, [revealedItems]);
  const topicsTrackingScope = historicalFullSessionReplay?.session_id || sessionId || `idle:${chatKey}`;

  useEffect(() => {
    const currentKeys = new Set(topicKeys);
    if (trackedTopicsRef.current.scope !== topicsTrackingScope) {
      trackedTopicsRef.current = { scope: topicsTrackingScope, keys: currentKeys };
      setTopicsPanelOpen(false);
      setUnreadTopicCount(0);
      return;
    }

    const addedCount = topicKeys.filter((key) => !trackedTopicsRef.current.keys.has(key)).length;
    trackedTopicsRef.current.keys = currentKeys;
    if (addedCount > 0 && !topicsPanelOpen) {
      setUnreadTopicCount((count) => count + addedCount);
    }
  }, [topicKeys, topicsPanelOpen, topicsTrackingScope]);

  const handleTopicsPanelToggle = useCallback((nextOpen) => {
    setTopicsPanelOpen(nextOpen);
    if (nextOpen) setUnreadTopicCount(0);
  }, []);
  const [historicalSpecificSkillReplay, setHistoricalSpecificSkillReplay] = useState(null);
  const [historicalSpecificStrategyReplay, setHistoricalSpecificStrategyReplay] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null); // 'persona' | 'metadata' | 'motivations' | 'barriers' | 'nextsteps' | 'transcript' | null
  const translations = LOCALE_MAP[language] ?? enTranslations;
  const t = (key, vars) => translate(translations, key, vars);
  useEffect(() => {
    if (!isSingleColumnViewport) {
      setActiveShellPanel('chat');
      setSingleColumnMenuOpen(false);
      setDrawerAccountMenuOpen(false);
    }
  }, [isSingleColumnViewport]);

  useEffect(() => {
    if (singleColumnMenuOpen) {
      setSingleColumnDrawerMounted(true);
      return undefined;
    }

    const timer = setTimeout(
      () => setSingleColumnDrawerMounted(false),
      SINGLE_COLUMN_DRAWER_ANIMATION_MS
    );
    return () => clearTimeout(timer);
  }, [singleColumnMenuOpen]);

  useEffect(() => {
    if (!singleColumnMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      const inDrawer = singleColumnMenuRef.current?.contains(event.target);
      const inAccountMenu = drawerAccountMenuPortalRef.current?.contains(event.target);
      if (!inDrawer && !inAccountMenu) {
        setSingleColumnMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [singleColumnMenuOpen]);

  useEffect(() => {
    if (!isSingleColumnViewport || !singleColumnMenuOpen) return undefined;

    const scrollY = window.scrollY;
    const { body, documentElement } = document;
    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyWidth = body.style.width;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = documentElement.style.overflow;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    documentElement.style.overflow = 'hidden';

    return () => {
      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.width = previousBodyWidth;
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousHtmlOverflow;
      // Only restore a real offset. Chrome for iOS insets its scroll view by
      // the browser-UI height, so scrollTo(0, 0) is not "go to the top" there —
      // it shoves the page up behind the omnibox. In the single-column shell
      // scrollY is 0 every time, so this would fire on every drawer close.
      if (scrollY) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [isSingleColumnViewport, singleColumnMenuOpen]);

  useEffect(() => {
    if (!drawerAccountMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      const inTrigger = drawerAccountMenuRef.current?.contains(event.target);
      const inPortal = drawerAccountMenuPortalRef.current?.contains(event.target);
      if (!inTrigger && !inPortal) {
        setDrawerAccountMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [drawerAccountMenuOpen]);

  const {
    menuOpen,
    menuRef,
    menuPortalRef,
    closeMenu,
    toggleMenu,
    theme,
    handleThemeChange,
    sidebarCollapsed,
    toggleSidebarCollapsed,
    insightsCollapsed,
    toggleInsightsCollapsed,
    userFirstName,
  } = useAppShell({
    authenticated,
    guest,
    preferredTheme,
    onThemeChange: setPreferredTheme,
  });
  const {
    notification,
    showNotification,
    closeNotification,
    confirmationDialog,
    showConfirm,
    handleConfirmResponse,
  } = useNotificationSystem();
  const resetFullSessionFeedbackState = useCallback(() => {
    setPostDownloadNextStepCue(false);
    setFeedbackPdfDownloaded(false);
    setCompletedFullSessionViewActive(false);
    setCompactPostSessionStatus(null);
    setFullSessionGuidanceVisible(false);
  }, []);

  const visibleFullSessionAnnotations = useMemo(() => {
    if (!fullSessionGuidanceVisible || !skillsAnnotationsEnabled) return [];
    const annotations = [];
    const seen = new Set();
    systemMessagesHistory.forEach((turn) => {
      (turn?.evaluation?.stimulus_annotations || []).forEach((annotation) => {
        const key = [
          annotation.turn_id,
          annotation.start,
          annotation.end,
          annotation.explanation,
        ].join('|');
        if (seen.has(key)) return;
        seen.add(key);
        annotations.push(annotation);
      });
    });
    return annotations;
  }, [fullSessionGuidanceVisible, skillsAnnotationsEnabled, systemMessagesHistory]);
  const resetConversationStateForView = useCallback(() => {
    setConversationStarted(false);
    setSystemMessagesHistory([]);
    setViewedTurnIndex(null);
    setRevealedItems(INITIAL_REVEALED_ITEMS);
    setStageMetrics(INITIAL_STAGE_METRICS);
    setHistoricalFullSessionReplay(null);
    setHistoricalSpecificSkillReplay(null);
    setHistoricalSpecificStrategyReplay(null);
    setFullSessionVignette(null);
  }, []);
  const {
    selectedProfile,
    setSelectedProfile,
    activeOverlay,
    overlayAnchor,
    showCustomBuilder,
    openOverlay,
    closeOverlay,
    showCustomBuilderView,
    hideCustomBuilderView,
    showHomeView,
    updateUrl,
  } = useAppViewState({
    resetConversationState: resetConversationStateForView,
    setSessionActive,
  });
  const {
    clientProfilesExtended,
    clientProfilesLoading,
    clientProfilesError,
    activeClientProfile,
    activeClientDisplayIndex,
    clientProfileVisible,
    highlightedMetaKey,
    setHighlightedMetaKey,
    personaList,
    motivationList,
    barrierList,
    nextSteps,
    metadataEntries,
    transcriptLines,
    handleClientProfileTabSelect,
    handleClientProfileReveal,
    resetClientProfileVisibility,
  } = useClientProfiles(activeOverlay);

  useEffect(() => {
    localStorage.removeItem('changetalk_conversation');
    localStorage.removeItem('changetalk_session_ended');
    localStorage.removeItem('changetalk_end_reason');
    localStorage.removeItem('changetalk_llm_mode');
  }, []);

  useEffect(() => {
    localStorage.setItem('theme', preferredTheme);
  }, [preferredTheme]);

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem('guided_highlights_enabled', String(guidedHighlightsEnabled));
  }, [guidedHighlightsEnabled]);

  useEffect(() => {
    localStorage.setItem('skills_annotations_enabled', String(skillsAnnotationsEnabled));
  }, [skillsAnnotationsEnabled]);

  useEffect(() => {
    localStorage.setItem('suggestions_enabled', String(suggestionsEnabled));
  }, [suggestionsEnabled]);

  useEffect(() => {
    localStorage.setItem('analytics_visible', String(preferredAnalyticsVisible));
  }, [preferredAnalyticsVisible]);

  useEffect(() => {
    if (!authenticated) {
      setPreferencesLoaded(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const prefs = await getUserPreferences();
        if (cancelled) return;
        if (prefs.theme) {
          setPreferredTheme(prefs.theme);
        }
        if (prefs.language) {
          setLanguage(prefs.language);
        }
        if (typeof prefs.guided_highlights_enabled === 'boolean') {
          setGuidedHighlightsEnabled(prefs.guided_highlights_enabled);
        }
        if (typeof prefs.skills_annotations_enabled === 'boolean') {
          setSkillsAnnotationsEnabled(prefs.skills_annotations_enabled);
        }
        if (typeof prefs.suggestions_enabled === 'boolean') {
          setSuggestionsEnabled(prefs.suggestions_enabled);
        }
        if (typeof prefs.analytics_visible === 'boolean') {
          setPreferredAnalyticsVisible(prefs.analytics_visible);
        }
        setUserAvatarPreference(normalizeUserAvatarPreference(prefs));
      } catch (err) {
        console.error('Failed to load user preferences:', err);
      } finally {
        if (!cancelled) {
          setPreferencesLoaded(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authenticated]);

  useEffect(() => {
    if (!authenticated || !preferencesLoaded) {
      return;
    }

    updateUserPreferences({
      theme: preferredTheme,
      language,
      guided_highlights_enabled: guidedHighlightsEnabled,
      skills_annotations_enabled: skillsAnnotationsEnabled,
      suggestions_enabled: suggestionsEnabled,
      analytics_visible: preferredAnalyticsVisible,
    }).catch((err) => {
      console.error('Failed to save user preferences:', err);
    });
  }, [
    authenticated,
    preferencesLoaded,
    preferredTheme,
    language,
    guidedHighlightsEnabled,
    skillsAnnotationsEnabled,
    suggestionsEnabled,
    preferredAnalyticsVisible,
  ]);

  const handleOpenOverlay = useCallback((overlayType, anchor = null) => {
    closeMenu();
    openOverlay(overlayType, anchor);
  }, [closeMenu, openOverlay]);

  const handleOpenCounsellorOverlay = useCallback((mode) => {
    handleOpenOverlay(getCounsellorOverlayFromMode(mode));
  }, [handleOpenOverlay]);

  const isDomEventLike = (value) => (
    value &&
    typeof value === 'object' &&
    (
      typeof value.preventDefault === 'function' ||
      typeof value.stopPropagation === 'function' ||
      value.nativeEvent
    )
  );

  const handleOpenAccountOverlay = useCallback(() => {
    closeMenu();
    openOverlay('account');
  }, [closeMenu, openOverlay]);

  const handleDownloadHistoricalReport = useCallback(async (historicalSessionId, options = {}) => {
    const blob = await downloadAccountFullSessionReport(historicalSessionId);
    const filename = `session-${historicalSessionId}-report.pdf`;
    if (options.openInNewTab) {
      openBlobDocument(blob, filename, options.pendingTab);
      return;
    }
    saveBlobAsDownload(blob, filename);
  }, []);

  const handleDownloadHistoricalTranscript = useCallback(async (historicalSessionId, options = {}) => {
    const blob = await downloadAccountFullSessionTranscript(historicalSessionId);
    const filename = `session-${historicalSessionId}-transcript.pdf`;
    if (options.openInNewTab) {
      openBlobDocument(blob, filename, options.pendingTab);
      return;
    }
    saveBlobAsDownload(blob, filename);
  }, []);

  const handleCloseOverlay = useCallback(() => {
    closeOverlay(resetClientProfileVisibility);
  }, [closeOverlay, resetClientProfileVisibility]);

  const clearHistoricalReplay = useCallback(() => {
    setHistoricalFullSessionReplay(null);
    setHistoricalSpecificSkillReplay(null);
    setHistoricalSpecificStrategyReplay(null);
    setSessionReportMode(null);
    setSessionCounsellorMode(null);
  }, []);

  useEffect(() => {
    if (!activeOverlay || !overlayAnchor) return;
    const frameId = window.requestAnimationFrame(() => {
      const target = document.getElementById(overlayAnchor);
      if (target) {
        scrollAncestorToTop(target);
      }
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [activeOverlay, overlayAnchor]);

  const {
    feedbackDialogOpen,
    feedbackContext,
    feedbackLikertResponse,
    setFeedbackLikertResponse,
    feedbackSkillPracticeResponse,
    setFeedbackSkillPracticeResponse,
    feedbackResistanceResponse,
    setFeedbackResistanceResponse,
    feedbackWordCount,
    feedbackText,
    setFeedbackText,
    countWords,
    feedbackSubmitting,
    openFeedbackDialog,
    closeFeedbackDialog,
    promptForCompletedSession,
    handleFeedbackSubmit,
  } = useFeedbackWorkflow({ showNotification });
  const contactOverlay = useContactOverlay({ activeOverlay, authenticated, t });

  const {
    oarsExercise,
    oarsExerciseLoading,
    oarsAttempt,
    oarsAnalyzing,
    setOarsAnalyzing,
    oarsStreamingEvaluation,
    setOarsStreamingEvaluation,
    oarsResetKey,
    oarsFilter,
    oarsExerciseIndex,
    specificSkillsAttemptCount,
    specificSkillsRetryAttemptId,
    setOarsPopupBlocking,
    oarsInfoOpen,
    setOarsInfoOpen,
    oarsInfoTab,
    oarsDismissSkillPopups,
    setOarsDismissSkillPopups,
    oarsInfoShowCheckbox,
    setOarsInfoShowCheckbox,
    oarsEffectivelyBlocking,
    oarsExerciseTotal,
    handleOarsInfoOpen,
    handleOarsTryAgain,
    handleOarsReplayAttempt,
    handleOarsLoadExercise,
    handleOarsNextExercise,
    handleOarsFilterChange,
    handleOarsAttempt,
    resetOarsState,
  } = useOarsFlow({
    selectedProfile,
    authenticated,
    openFeedbackDialog,
  });
  const specificStrategiesFlow = useSpecificStrategiesFlow();

  const handleFreshOarsFilterChange = useCallback(async (category) => {
    clearHistoricalReplay();
    return handleOarsFilterChange(category);
  }, [clearHistoricalReplay, handleOarsFilterChange]);

  useEffect(() => {
    if (isSingleColumnViewport && selectedProfile === -3 && oarsAnalyzing) {
      setSingleColumnMenuOpen(true);
      setDrawerAccountMenuOpen(false);
    }
  }, [isSingleColumnViewport, oarsAnalyzing, selectedProfile]);

  const {
    resetConversationState,
    clearCurrentSessionData,
    clearSessionSelection,
    startSelectedProfileSession,
    confirmSessionTransition,
  } = useSessionTransition({
    sessionActive,
    sessionId,
    conversationStarted,
    showConfirm,
    completedFullSessionViewActive,
    feedbackPdfDownloaded,
    setSelectedProfile,
    setSessionActive,
    setSessionId,
    setConversationStarted,
    setSystemMessagesHistory,
    setViewedTurnIndex,
    setRevealedItems,
    setStageMetrics,
  });

  const handleViewHistoricalFullSession = useCallback(async (historicalSessionId) => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Opening a past session will close this completed session view.'
    );
    const canOpenReplay = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Opening a past session will end the current conversation view.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Opening a past session will clear the current session data.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
      clearLocalStateWhenIdle: true,
    });

    if (!canOpenReplay) {
      return;
    }

    const replayData = await getAccountFullSessionReplay(historicalSessionId);
    const session = replayData.session;
    const parsedProfileId = Number.parseInt(session?.profile_id, 10);
    const profileId = Number.isFinite(parsedProfileId) ? parsedProfileId : null;

    setChatKey((k) => k + 1);
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    setBtcCueFlowActive(false);
    setSessionActive(false);
    setSessionId(null);
    resetOarsState();
    setCustomProfile(null);
    setHistoricalSpecificSkillReplay(null);
    setHistoricalFullSessionReplay({
      ...session,
      profile_id: profileId,
    });
    setSessionReportMode(session?.mode || null);
    // Every session row carries a counsellor_mode (the backend defaults it at
    // /session/start), so it only describes a real counsellor when the session
    // actually ran one. Same guard the account card uses to label the session.
    setSessionCounsellorMode(
      session?.mode === 'llm_mode' ? (session.counsellor_mode || null) : null
    );
    setConversationStarted(true);
    setSystemMessagesHistory(session.system_messages_history || []);
    setViewedTurnIndex(null);
    setRevealedItems(session.revealed_items || INITIAL_REVEALED_ITEMS);
    setStageMetrics(session.stage_metrics || INITIAL_STAGE_METRICS);
    setSelectedProfile(profileId);
    handleCloseOverlay();
  }, [
    confirmSessionTransition,
    resetFullSessionFeedbackState,
    selectedProfile,
    resetOarsState,
    handleCloseOverlay,
    setSelectedProfile,
  ]);

  const handleViewHistoricalSpecificSkillAttempt = useCallback(async (attemptId) => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : id === -3 ? 'Specific Skills' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Opening a past specific skills attempt will close this completed session view.'
    );
    const canOpenReplay = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Opening a past specific skills attempt will end the current conversation view.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Opening a past specific skills attempt will clear the current session data.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
      clearLocalStateWhenIdle: true,
    });

    if (!canOpenReplay) {
      return;
    }

    const replayData = await getAccountSpecificSkillReplay(attemptId);

    setChatKey((k) => k + 1);
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    setBtcCueFlowActive(false);
    setSessionActive(false);
    setSessionId(null);
    clearHistoricalReplay();
    setCustomProfile(null);
    setConversationStarted(false);
    setSystemMessagesHistory([]);
    setViewedTurnIndex(null);
    setRevealedItems(INITIAL_REVEALED_ITEMS);
    setStageMetrics(INITIAL_STAGE_METRICS);
    setSelectedProfile(-3);
    handleOarsReplayAttempt(replayData.exercise, replayData.attempt);
    setHistoricalSpecificSkillReplay({
      attemptId,
      exerciseId: replayData.exercise?.id || replayData.attempt?.exercise_id,
    });
    handleCloseOverlay();
    updateUrl(null, -3, true);
  }, [
    clearHistoricalReplay,
    confirmSessionTransition,
    handleCloseOverlay,
    handleOarsReplayAttempt,
    resetFullSessionFeedbackState,
    selectedProfile,
    setSelectedProfile,
    updateUrl,
  ]);

  const handleViewHistoricalSpecificStrategyAttempt = useCallback(async (attemptId) => {
    const canOpenReplay = await confirmSessionTransition({
      activeMessage:
        'Opening a past Specific Strategies attempt will end the current conversation view.\n\n' +
        'Do you want to continue?',
      analysisMessage:
        'Session analysis may still be in progress.\n\n' +
        'Opening a past Specific Strategies attempt will clear the current session data.\n\n' +
        'Do you want to continue?',
      ...buildCompletedSessionMessages(
        'Your completed session',
        'Opening a past Specific Strategies attempt will close this completed session view.'
      ),
      clearLocalStateWhenIdle: true,
    });

    if (!canOpenReplay) return;

    const replayData = await getAccountSpecificStrategyReplay(attemptId);
    setChatKey((key) => key + 1);
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    setBtcCueFlowActive(false);
    setSessionActive(false);
    setSessionId(null);
    clearHistoricalReplay();
    resetOarsState();
    setCustomProfile(null);
    setConversationStarted(false);
    setSystemMessagesHistory([]);
    setViewedTurnIndex(null);
    setRevealedItems(INITIAL_REVEALED_ITEMS);
    setStageMetrics(INITIAL_STAGE_METRICS);
    specificStrategiesFlow.loadReplay(replayData);
    setHistoricalSpecificStrategyReplay({ attemptId });
    setSelectedProfile(-4);
    handleCloseOverlay();
    updateUrl(null, -4, true);
  }, [
    clearHistoricalReplay,
    confirmSessionTransition,
    handleCloseOverlay,
    resetFullSessionFeedbackState,
    resetOarsState,
    setSelectedProfile,
    specificStrategiesFlow,
    updateUrl,
  ]);

  const handleReturnToAccountFromReplay = useCallback(() => {
    clearHistoricalReplay();
    clearSessionSelection();
    handleOpenAccountOverlay();
  }, [clearHistoricalReplay, clearSessionSelection, handleOpenAccountOverlay]);

  const handleReturnToAccountFromSpecificSkillReplay = useCallback(() => {
    setHistoricalSpecificSkillReplay(null);
    resetOarsState();
    clearSessionSelection();
    handleOpenAccountOverlay();
  }, [clearSessionSelection, handleOpenAccountOverlay, resetOarsState]);

  const handleReturnToAccountFromSpecificStrategyReplay = useCallback(() => {
    setHistoricalSpecificStrategyReplay(null);
    specificStrategiesFlow.resetFlow();
    clearSessionSelection();
    handleOpenAccountOverlay();
  }, [clearSessionSelection, handleOpenAccountOverlay, specificStrategiesFlow]);

  const handleSpecificStrategyTryAgain = useCallback(() => {
    setHistoricalSpecificStrategyReplay(null);
    specificStrategiesFlow.retryExercise();
  }, [specificStrategiesFlow]);

  const handleOarsReplayTryAgain = useCallback(() => {
    setHistoricalSpecificSkillReplay(null);
    handleOarsTryAgain();
    updateUrl(null, -3, true);
  }, [handleOarsTryAgain, updateUrl]);

  const handleGoHome = useCallback(async () => {
    // Already on home screen with no session, no overlay, and no builder
    if (selectedProfile === null && !sessionId && !activeOverlay && !showCustomBuilder) return;

    if (showCustomBuilder) {
      hideCustomBuilderView(selectedProfile > 0 ? selectedProfile : null);
      return;
    }

    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Returning to the home screen will close this completed session view.'
    );
    const canLeaveSession = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Returning to the home screen will end the current conversation.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Returning to the home screen will clear the current session data.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
    });

    if (!canLeaveSession) {
      return;
    }

    if (selectedProfile === -2 && sessionId) {
      await resetSession().catch(err => console.error('Error resetting session:', err));
    }

    setChatKey(k => k + 1);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    clearSessionSelection();
    showHomeView();
  }, [activeOverlay, clearHistoricalReplay, clearSessionSelection, confirmSessionTransition, hideCustomBuilderView, resetFullSessionFeedbackState, selectedProfile, sessionId, showCustomBuilder, showHomeView]);

  const handleSignOut = useCallback(async () => {
    const completedMessages = buildCompletedSessionMessages(
      'Your completed session',
      'Signing out will close this completed session view.'
    );
    const canSignOut = await confirmSessionTransition({
      activeMessage:
        'You are currently in a session.\n\nSigning out will end the current conversation.\n\nDo you want to continue?',
      analysisMessage:
        'Session analysis may still be in progress.\n\nSigning out will clear the current session data.\n\nDo you want to continue?',
      ...completedMessages,
    });

    if (!canSignOut) {
      return;
    }

    setChatKey(k => k + 1);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    clearSessionSelection();
    showHomeView();
    signOut();
  }, [clearHistoricalReplay, clearSessionSelection, confirmSessionTransition, resetFullSessionFeedbackState, showHomeView, signOut]);

  // Navigate to the custom client builder page
  const handleNavigateToBuilder = useCallback(async () => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Going to the Custom Client Builder will close this completed session view.'
    );
    const canOpenBuilder = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Going to the Custom Client Builder will end the current conversation.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Going to the Custom Client Builder will clear the current session data.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
      clearLocalStateWhenIdle: true,
    });

    if (!canOpenBuilder) {
      return;
    }

    if (sessionActive || sessionId) {
      clearCurrentSessionData();
    }

    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    closeMenu();
    showCustomBuilderView();
  }, [clearCurrentSessionData, clearHistoricalReplay, closeMenu, confirmSessionTransition, resetFullSessionFeedbackState, selectedProfile, sessionActive, sessionId, showCustomBuilderView]);

  // Called when user clicks Start Session inside the builder
  const handleStartCustomSession = useCallback(async (profile) => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Starting a new Custom Client session will close this completed session view.'
    );
    const canStartSession = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Starting a new Custom Client session will end the current conversation.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Starting a new Custom Client session will clear the current session data.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
    });

    if (!canStartSession) {
      return;
    }

    setCustomProfile(profile);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    startSelectedProfileSession(-1);
    hideCustomBuilderView(-1);
  }, [clearHistoricalReplay, confirmSessionTransition, hideCustomBuilderView, resetFullSessionFeedbackState, selectedProfile, startSelectedProfileSession]);

  // Handle "Act as Client" button in the sidebar
  const handleActAsClient = useCallback(async (counsellorMode = null) => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Starting a Client Mode session will close this completed session view.'
    );
    const canEnterClientMode = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Starting a Client Mode session will end the current conversation.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Starting a Client Mode session will clear the current session data.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
    });

    if (!canEnterClientMode) {
      return;
    }

    if (sessionId) {
      await resetSession().catch(err => console.error('Error resetting session:', err));
    }
    setChatKey(k => k + 1);  // remount ChatInterface for clean slate
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    setBtcCueFlowActive(true);
    setClientModeCounsellorMode(counsellorMode);
    startSelectedProfileSession(-2);   // sentinel for client mode
    setCustomProfile(null);
    updateUrl(null, -2);
  }, [clearHistoricalReplay, confirmSessionTransition, resetFullSessionFeedbackState, selectedProfile, sessionId, startSelectedProfileSession, updateUrl]);

  const handleBtcCounsellorSelection = useCallback(async (counsellorMode) => {
    const counsellorName = (mode) => {
      if (mode === 'volley') return 'Freeform';
      if (mode === 'utterance_multiBC') return 'Technique-Guided';
      if (mode === 'stage_guided_multiBC') return 'Stage-Guided';
      return 'selected';
    };

    if (selectedProfile !== -2) {
      setClientModeCounsellorMode(counsellorMode);
      return;
    }

    const hasSessionData = sessionActive || sessionId;
    const switchingCounsellors = clientModeCounsellorMode !== counsellorMode;

    if (switchingCounsellors && hasSessionData) {
      const currentLabel = counsellorName(clientModeCounsellorMode);
      const nextLabel = counsellorName(counsellorMode);
      const completedMessages = buildCompletedSessionMessages(
        `Your session with the ${currentLabel} counsellor`,
        `Switching to the ${nextLabel} counsellor will close this completed session view.`
      );
      const canSwitchCounsellor = await confirmSessionTransition({
        activeMessage:
          `You are currently in a session with the ${currentLabel} counsellor.\n\n` +
          `Switching to the ${nextLabel} counsellor will end the current conversation.\n\n` +
          `Do you want to continue?`,
        analysisMessage:
          `${currentLabel} counsellor session analysis may still be in progress.\n\n` +
          `Switching to the ${nextLabel} counsellor will clear the current session data.\n\n` +
          `Do you want to continue?`,
        ...completedMessages,
        clearLocalStateWhenIdle: true,
      });

      if (!canSwitchCounsellor) {
        return;
      }
    } else if (!switchingCounsellors && hasSessionData) {
      return;
    }

    if (sessionId) {
      await resetSession().catch(err => console.error('Error resetting session:', err));
    }
    setChatKey(k => k + 1);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    setBtcCueFlowActive(true);
    setClientModeCounsellorMode(counsellorMode);
    startSelectedProfileSession(-2);
    setCustomProfile(null);
    setBtcActivationRequest((value) => value + 1);
    updateUrl(null, -2);
  }, [clearHistoricalReplay, clientModeCounsellorMode, confirmSessionTransition, resetFullSessionFeedbackState, selectedProfile, sessionActive, sessionId, startSelectedProfileSession, updateUrl]);

  useEffect(() => {
    if (selectedProfile !== -2 && btcCueFlowActive) {
      setBtcCueFlowActive(false);
    }
  }, [selectedProfile, btcCueFlowActive]);

  const handleEnterOars = useCallback(async () => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Starting OARS mode will close this completed session view.'
    );
    const canEnterOars = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\nStarting OARS mode will end the current conversation.\n\nDo you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\nStarting OARS mode will clear the current session data.\n\nDo you want to continue?`,
      ...completedMessages,
    });
    if (!canEnterOars) return;

    if (sessionActive || sessionId) {
      clearCurrentSessionData();
    }

    setChatKey(k => k + 1);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    startSelectedProfileSession(-3);
    setCustomProfile(null);
    setSessionActive(false);
    resetOarsState();
    updateUrl(null, -3);
  }, [clearCurrentSessionData, clearHistoricalReplay, confirmSessionTransition, resetFullSessionFeedbackState, resetOarsState, selectedProfile, sessionActive, sessionId, startSelectedProfileSession, updateUrl]);

  const handleEnterStrategies = useCallback(async () => {
    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : id === -3 ? 'Specific Skills' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Opening Specific Strategies will close this completed session view.'
    );
    const canEnterStrategies = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\nOpening Specific Strategies will end the current conversation.\n\nDo you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\nOpening Specific Strategies will clear the current session data.\n\nDo you want to continue?`,
      ...completedMessages,
    });
    if (!canEnterStrategies) return;

    if (sessionActive || sessionId) {
      clearCurrentSessionData();
    }

    setChatKey((key) => key + 1);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    startSelectedProfileSession(-4);
    setCustomProfile(null);
    setSessionActive(false);
    resetOarsState();
    specificStrategiesFlow.resetFlow();
    updateUrl(null, -4);
  }, [clearCurrentSessionData, clearHistoricalReplay, confirmSessionTransition, resetFullSessionFeedbackState, resetOarsState, selectedProfile, sessionActive, sessionId, specificStrategiesFlow, startSelectedProfileSession, updateUrl]);

  const handleTryGeneratedOarsExercise = useCallback(async (exercise) => {
    if (!exercise) {
      return;
    }

    const clientLabel = (id) => id === -1 ? 'Custom Client' : id === -2 ? 'Client Mode' : id === -3 ? 'Specific Skills' : `Virtual Client ${id}`;
    const completedMessages = buildCompletedSessionMessages(
      `Your session with ${clientLabel(selectedProfile)}`,
      'Trying this exercise will close this completed session view and open Specific Skills mode.'
    );
    const canEnterOars = await confirmSessionTransition({
      activeMessage:
        `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
        `Trying this exercise will open it in Specific Skills mode and end the current conversation.\n\n` +
        `Do you want to continue?`,
      analysisMessage:
        `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
        `Trying this exercise will clear the current session data and open Specific Skills mode.\n\n` +
        `Do you want to continue?`,
      ...completedMessages,
    });
    if (!canEnterOars) return;

    if (sessionActive || sessionId) {
      clearCurrentSessionData();
    }

    setChatKey(k => k + 1);
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    startSelectedProfileSession(-3);
    setCustomProfile(null);
    setSessionActive(false);
    handleOarsLoadExercise(exercise);
    handleCloseOverlay();
    updateUrl(null, -3);
  }, [
    clearCurrentSessionData,
    clearHistoricalReplay,
    confirmSessionTransition,
    handleCloseOverlay,
    handleOarsLoadExercise,
    resetFullSessionFeedbackState,
    selectedProfile,
    sessionActive,
    sessionId,
    startSelectedProfileSession,
    updateUrl,
  ]);

  // When user selects a client profile
  const handleProfileSelect = async (profileIdx) => {
    // If clicking the same client while analysis is running, do nothing
    if (selectedProfile === profileIdx && sessionId && !sessionActive) {
      // Analysis is in progress for the same client - ignore the click
      return false;
    }

    const switchingClients = selectedProfile !== null && selectedProfile !== profileIdx;
    const hasSessionData = sessionActive || sessionId;

    // If already in an active session OR viewing results/analysis with a different client
    if (switchingClients && hasSessionData) {
      const clientLabel = (id) => id === -1 ? 'Custom Client' : `Virtual Client ${id}`;
      const completedMessages = buildCompletedSessionMessages(
        `Your session with ${clientLabel(selectedProfile)}`,
        `Switching to ${clientLabel(profileIdx)} will close this completed session view.`
      );
      const canSwitchClient = await confirmSessionTransition({
        activeMessage:
          `You are currently in a session with ${clientLabel(selectedProfile)}.\n\n` +
          `Switching to ${clientLabel(profileIdx)} will end the current conversation.\n\n` +
          `Do you want to continue?`,
        analysisMessage:
          `${clientLabel(selectedProfile)} session analysis may still be in progress.\n\n` +
          `Switching to ${clientLabel(profileIdx)} will clear the current session data.\n\n` +
          `Do you want to continue?`,
        ...completedMessages,
        clearLocalStateWhenIdle: true,
      });

      if (!canSwitchClient) {
        return false;
      }
    }

    setCustomProfile(null);
    clearHistoricalReplay();
      resetFullSessionFeedbackState();
    startSelectedProfileSession(profileIdx);
    // Update URL to reflect selected client
    updateUrl(null, profileIdx);
    return true;
  };

  // When session ends
  const handleSessionEnd = (sessionId, options = {}) => {
    setSessionId(sessionId);
    setSessionReportMode(options.mode || null);
    setSessionCounsellorMode(options.counsellorMode || null);
    setSessionActive(false);
    if (selectedProfile === -2) {
      setBtcPostSessionNextStepCue(true);
    }
    promptForCompletedSession(selectedProfile);
  };

  // Clear all insights/session state — called on restart from either ChatInterface or InsightsSidebar
  const resetSessionInsights = useCallback(() => {
    clearHistoricalReplay();
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    resetConversationState();
  }, [clearHistoricalReplay, resetConversationState, resetFullSessionFeedbackState]);

  // Restart session with same client (called from InsightsSidebar)
  const handleRestartSession = useCallback(() => {
    resetSessionInsights();
    setSessionActive(true);
  }, [resetSessionInsights]);

  const handleReportRestartSession = useCallback(async () => {
    if (selectedProfile !== -2) {
      handleRestartSession();
      return;
    }

    if (!clientModeCounsellorMode) {
      handleRestartSession();
      return;
    }

    await resetSession().catch(err => console.error('Error resetting session:', err));
    setChatKey(k => k + 1);
    resetFullSessionFeedbackState();
    setBtcPostSessionNextStepCue(false);
    setBtcCueFlowActive(true);
    startSelectedProfileSession(-2);
    setCustomProfile(null);
    setBtcActivationRequest((value) => value + 1);
    updateUrl(null, -2);
  }, [selectedProfile, clientModeCounsellorMode, updateUrl, handleRestartSession, resetFullSessionFeedbackState, startSelectedProfileSession]);

  // Update system messages from ChatInterface - append new turn to history
  const handleSystemMessagesUpdate = (
    counsellorMsgs,
    clientMsgs,
    turnStageMetrics,
    turnWorkingAllianceSeries = [],
    turnAllTasksSeries = [],
    turnCoaching = null
  ) => {
    const hasWorkingAllianceData = Array.isArray(turnWorkingAllianceSeries) && turnWorkingAllianceSeries.length > 0;
    const hasAllTasksData = Array.isArray(turnAllTasksSeries) && turnAllTasksSeries.length > 0;
    const hasStageMetricsData = Boolean(
      turnStageMetrics &&
      Object.values(turnStageMetrics).some(value => value !== null && value !== undefined)
    );
    const newTurn = {
      counsellor: counsellorMsgs || [],
      client: clientMsgs || [],
      stageMetrics: turnStageMetrics || null,
      workingAllianceSeries: turnWorkingAllianceSeries || [],
      allTasksSeries: turnAllTasksSeries || [],
      automitiLabels: turnCoaching?.automitiLabels || [],
      evaluation: turnCoaching?.evaluation || null,
      traineeText: turnCoaching?.traineeText || '',
      evaluationLoading: false,
      fullSessionProvisional: false,
      counsellorIsLlm: Boolean(turnCoaching?.counsellorIsLlm),
    };
    // Only add if there's actual content
    if (newTurn.counsellor.length > 0 || newTurn.client.length > 0 || hasStageMetricsData || hasWorkingAllianceData || hasAllTasksData) {
      setSystemMessagesHistory((prev) => {
        if (prev[prev.length - 1]?.fullSessionProvisional) {
          return [...prev.slice(0, -1), { ...prev[prev.length - 1], ...newTurn }];
        }
        return [...prev, newTurn];
      });
      // Auto-advance to latest turn
      setViewedTurnIndex(null);
    }
  };

  const handleFullSessionClassification = useCallback((automitiLabels, { counsellorIsLlm = false } = {}) => {
    setSystemMessagesHistory((previous) => [
      ...previous,
      {
        // Carry the latest trends forward until this turn's metadata lands, so the
        // WAI chart does not blank out while the client reply is still streaming.
        workingAllianceSeries: previous[previous.length - 1]?.workingAllianceSeries || [],
        allTasksSeries: previous[previous.length - 1]?.allTasksSeries || [],
        counsellor: [],
        client: [],
        automitiLabels: automitiLabels || [],
        evaluation: null,
        // LLM counsellor turns get no guidance, so there is nothing left to load.
        evaluationLoading: !counsellorIsLlm,
        counsellorIsLlm,
        fullSessionProvisional: true,
        stageMetrics: null,
      },
    ]);
    setViewedTurnIndex(null);
  }, []);

  const handleFullSessionGuidance = useCallback((feedback) => {
    setSystemMessagesHistory((previous) => {
      if (!previous.length) return previous;
      const latest = previous[previous.length - 1];
      return [
        ...previous.slice(0, -1),
        { ...latest, evaluation: { feedback }, evaluationLoading: true },
      ];
    });
  }, []);

  const handleFullSessionGuidanceError = useCallback((guidanceError) => {
    setSystemMessagesHistory((previous) => {
      if (!previous.length) return previous;
      const latest = previous[previous.length - 1];
      return [
        ...previous.slice(0, -1),
        { ...latest, guidanceError, evaluationLoading: false },
      ];
    });
  }, []);

  // Update revealed items from ChatInterface
  const handleRevealedItemsUpdate = (items) => {
    setRevealedItems(items || INITIAL_REVEALED_ITEMS);
  };

  // Update stage metrics from ChatInterface
  const handleStageMetricsUpdate = (metrics) => {
    setStageMetrics(metrics || INITIAL_STAGE_METRICS);
  };

  const shellSidebarCollapsed = isSingleColumnViewport ? false : sidebarCollapsed;
  const shellInsightsCollapsed = isSingleColumnViewport ? false : insightsCollapsed;
  const layoutClasses = ['App-layout'];
  if (shellSidebarCollapsed) layoutClasses.push('sidebar-collapsed');
  if (shellInsightsCollapsed) layoutClasses.push('insights-collapsed');
  if (isSingleColumnViewport) {
    layoutClasses.push('single-column-layout', `active-panel-${activeShellPanel}`);
  }
  const appClasses = ['App', theme];
  if (isSingleColumnViewport) appClasses.push('App--single-column');
  if (isSingleColumnViewport && singleColumnMenuOpen) appClasses.push('App--single-column-drawer-open');
  if (
    isSingleColumnViewport
    && (oarsInfoOpen || specificStrategiesFlow.strategyInfoOpen)
  ) appClasses.push('App--single-column-skill-popup-open');
  if (isMobileViewport) appClasses.push('App--mobile-shell');
  const activePracticeMode = selectedProfile === -4 ? 'strategies' : selectedProfile === -3 ? 'oars' : selectedProfile === -2 ? 'btc' : 'freeform';
  const drawerAccountName = userFirstName || 'Account';
  const closeSingleColumnDrawer = useCallback(() => {
    setSingleColumnMenuOpen(false);
    setDrawerAccountMenuOpen(false);
  }, []);
  // Swiping right anywhere on the screen opens the drawer, same as the hamburger.
  const openSingleColumnDrawer = useCallback(() => {
    setSingleColumnMenuOpen(true);
  }, []);
  useRightSwipe({
    enabled: (
      isSingleColumnViewport &&
      !singleColumnMenuOpen &&
      !activeOverlay &&
      !showCustomBuilder &&
      !oarsInfoOpen &&
      !specificStrategiesFlow.strategyInfoOpen
    ),
    onSwipe: openSingleColumnDrawer,
  });
  const singleColumnMenuNeedsSelection = (
    isSingleColumnViewport &&
    guidedHighlightsEnabled &&
    (
      (selectedProfile === null || selectedProfile === undefined) ||
      (selectedProfile === -2 && !clientModeCounsellorMode) ||
      (selectedProfile === -3 && !oarsExercise && !oarsExerciseLoading) ||
      (
        selectedProfile === -4
        && (!specificStrategiesFlow.selectedTask || !specificStrategiesFlow.selectedStrategy)
      )
    )
  );
  const singleColumnSpecificSkillsPostAttemptCue = (
    isSingleColumnViewport &&
    guidedHighlightsEnabled &&
    selectedProfile === -3 &&
    !historicalSpecificSkillReplay &&
    !!oarsAttempt &&
    !singleColumnMenuOpen
  );
  const singleColumnSpecificStrategiesFeedbackCue = (
    isSingleColumnViewport &&
    guidedHighlightsEnabled &&
    selectedProfile === -4 &&
    specificStrategiesFlow.turnResults.length > 0 &&
    !singleColumnMenuOpen
  );
  const singleColumnPanelMenuNeedsCue = (
    singleColumnMenuNeedsSelection
    || singleColumnSpecificSkillsPostAttemptCue
    || singleColumnSpecificStrategiesFeedbackCue
  );
  const chatPanelHasHeader = (
    selectedProfile !== null &&
    selectedProfile !== undefined &&
    (
      selectedProfile === -3
        ? Boolean(oarsExercise || oarsExerciseLoading)
        : selectedProfile === -4
          ? Boolean(specificStrategiesFlow.exercise || specificStrategiesFlow.loading)
        : selectedProfile === -2
          ? Boolean(sessionId)
          : true
    )
  );
  const singleColumnPanelMenuClasses = ['single-column-panel-menu'];
  if (activeShellPanel === 'chat' && chatPanelHasHeader) {
    singleColumnPanelMenuClasses.push('single-column-panel-menu--below-chat-header');
  }
  const clientSidebarProps = {
    theme,
    selectedProfile,
    showPostDownloadNextStepCue: guidedHighlightsEnabled && postDownloadNextStepCue,
    disableFullSessionCues: !guidedHighlightsEnabled,
    disableSpecificSkillsStageOneCue: !guidedHighlightsEnabled,
    suppressTransientNotices: !suggestionsEnabled || isSingleColumnViewport || activeOverlay === 'account',
    onProfileSelect: handleProfileSelect,
    onShowClientInfo: () => handleOpenOverlay('client'),
    onShowWorkingAlliance: () => handleOpenOverlay('working-alliance'),
    onShowCounsellors: () => handleOpenCounsellorOverlay(clientModeCounsellorMode),
    onCustomClient: handleNavigateToBuilder,
    clientModeCounsellorMode,
    onClientModeCounsellorModeChange: handleBtcCounsellorSelection,
    showBtcCounsellorCue: (
      guidedHighlightsEnabled &&
      selectedProfile === -2 &&
      (
        !clientModeCounsellorMode ||
        btcPostSessionNextStepCue
      )
    ),
    oarsExercise: oarsEffectivelyBlocking ? null : oarsExercise,
    oarsExerciseLoading: selectedProfile === -3 ? oarsExerciseLoading : false,
    oarsAttempt: selectedProfile === -3 ? oarsAttempt : undefined,
    oarsAnalyzing: selectedProfile === -3 ? oarsAnalyzing : undefined,
    oarsStreamingEvaluation: selectedProfile === -3 ? oarsStreamingEvaluation : undefined,
    showSpecificSkillsPostAttemptCue: guidedHighlightsEnabled && selectedProfile === -3 && !historicalSpecificSkillReplay ? !!oarsAttempt : false,
    oarsFilter: selectedProfile === -3 ? oarsFilter : undefined,
    onOarsFilterChange: selectedProfile === -3 ? handleFreshOarsFilterChange : undefined,
    onOarsInfoOpen: selectedProfile === -3 ? handleOarsInfoOpen : undefined,
    onStrategyInfoOpen: selectedProfile === -4 ? specificStrategiesFlow.openStrategyInfo : undefined,
    oarsInfoOpen: selectedProfile === -3 ? oarsInfoOpen : undefined,
    oarsInfoTab: selectedProfile === -3 ? oarsInfoTab : undefined,
    onOarsInfoClose: selectedProfile === -3 ? () => setOarsInfoOpen(false) : undefined,
    authenticated,
    strategiesSelectedTask: specificStrategiesFlow.selectedTask,
    strategiesSelectedStrategy: specificStrategiesFlow.selectedStrategy,
    strategiesExerciseStatus: specificStrategiesFlow.status,
    strategiesFlow: specificStrategiesFlow,
    onStrategiesTaskChange: specificStrategiesFlow.selectTask,
    onStrategiesStrategyChange: specificStrategiesFlow.selectStrategy,
  };
  const drawerClientSidebarProps = {
    ...clientSidebarProps,
    onProfileSelect: async (profileId) => {
      const didSelect = await handleProfileSelect(profileId);
      if (didSelect !== false) {
        setSingleColumnMenuOpen(false);
      }
      return didSelect;
    },
    onCustomClient: () => {
      setSingleColumnMenuOpen(false);
      handleNavigateToBuilder();
    },
    hideCustomClientOption: true,
    compactPostSessionStatus: selectedProfile !== null && selectedProfile !== undefined && selectedProfile !== -3 && selectedProfile !== -4 && compactPostSessionStatus
      ? {
          ...compactPostSessionStatus,
          onReturnToAccount: compactPostSessionStatus.onReturnToAccount
            ? () => {
                closeSingleColumnDrawer();
                compactPostSessionStatus.onReturnToAccount();
              }
            : undefined,
        }
      : null,
    compactSessionObserverStatus: selectedProfile !== null && selectedProfile !== undefined && selectedProfile !== -3 && selectedProfile !== -4
      ? compactSessionObserverStatus
      : null,
    fullSessionVignette,
    showFullSessionClientDescription: (
      selectedProfile !== null &&
      selectedProfile !== undefined &&
      selectedProfile > 0 &&
      sessionActive &&
      !conversationStarted &&
      Boolean(fullSessionVignette?.patient_description)
    ),
    onShowWorkingAlliance: () => {
      closeSingleColumnDrawer();
      handleOpenOverlay('working-alliance');
    },
    showOarsConversationContext: selectedProfile === -3,
    onOarsNextExercise: selectedProfile === -3
      ? async () => {
          await handleOarsNextExercise();
          setSingleColumnMenuOpen(false);
        }
      : undefined,
    onOarsTryAgain: selectedProfile === -3
      ? () => {
          if (historicalSpecificSkillReplay) {
            handleOarsReplayTryAgain();
          } else {
            handleOarsTryAgain();
          }
          setSingleColumnMenuOpen(false);
        }
      : undefined,
    isSpecificSkillReplay: Boolean(historicalSpecificSkillReplay),
    showSpecificSkillsActionCue: guidedHighlightsEnabled && selectedProfile === -3 ? !!oarsAttempt : false,
    onReturnToAccountFromSpecificSkillReplay: () => {
      closeSingleColumnDrawer();
      handleReturnToAccountFromSpecificSkillReplay();
    },
    onShowAutomatedMiscMitiCounsellor: selectedProfile === -3 || selectedProfile === -4
      ? () => {
          closeSingleColumnDrawer();
          handleOpenOverlay('automated-misc-miti', 'counsellor-miti-codes');
        }
      : undefined,
    onClientModeCounsellorModeChange: (mode) => {
      handleBtcCounsellorSelection(mode);
      setSingleColumnMenuOpen(false);
    },
    onOarsFilterChange: selectedProfile === -3
      ? (filter) => {
          handleFreshOarsFilterChange(filter);
          setSingleColumnMenuOpen(false);
        }
      : undefined,
    showStrategiesDrawerDetails: selectedProfile === -4,
    onStrategiesStrategyChange: selectedProfile === -4
      ? (strategy) => {
          const selection = specificStrategiesFlow.selectStrategy(strategy);
          setSingleColumnMenuOpen(false);
          return selection;
        }
      : undefined,
    onSpecificStrategyTryAgain: selectedProfile === -4
      ? () => {
          if (historicalSpecificStrategyReplay) {
            handleSpecificStrategyTryAgain();
          } else {
            specificStrategiesFlow.retryExercise();
          }
          setSingleColumnMenuOpen(false);
        }
      : undefined,
    onStrategiesNextExercise: selectedProfile === -4
      ? () => {
          const selection = specificStrategiesFlow.selectStrategy('Scaling Questions');
          setSingleColumnMenuOpen(false);
          return selection;
        }
      : undefined,
    isSpecificStrategyReplay: Boolean(historicalSpecificStrategyReplay),
    onReturnToAccountFromSpecificStrategyReplay: () => {
      closeSingleColumnDrawer();
      handleReturnToAccountFromSpecificStrategyReplay();
    },
  };

  const renderOverlayRouter = () => (
    <Suspense fallback={null}>
      <OverlayRouter
        activeOverlay={activeOverlay}
        closeOverlay={handleCloseOverlay}
        t={t}
        accountOverlay={{
          authenticated,
          userFirstName,
          userAvatarPreference,
          onUserAvatarPreferenceChange: (nextPreference) => {
            setUserAvatarPreference(normalizeUserAvatarPreference(nextPreference));
          },
          onViewFullSession: handleViewHistoricalFullSession,
          onViewSpecificSkillAttempt: handleViewHistoricalSpecificSkillAttempt,
          onViewSpecificStrategyAttempt: handleViewHistoricalSpecificStrategyAttempt,
        }}
        overlayUi={{
          setExpandedSection,
          onTryGeneratedOarsExercise: handleTryGeneratedOarsExercise,
        }}
        miscCodingOverlay={{
          clientCodingRows,
          counsellorCodingRows,
        }}
        contactOverlay={{
          ...contactOverlay,
        }}
        clientProfilesOverlay={{
          setExpandedSection,
          clientProfilesLoading,
          clientProfilesError,
          clientProfilesExtended,
          activeClientProfile,
          activeClientDisplayIndex,
          handleClientProfileTabSelect,
          clientProfileVisible,
          personaList,
          getMetaKeyForPersona,
          highlightedMetaKey,
          setHighlightedMetaKey,
          metadataEntries,
          motivationList,
          barrierList,
          nextSteps,
          transcriptLines,
          handleClientProfileReveal,
        }}
        theme={theme}
        userAvatarPreference={userAvatarPreference}
      />
    </Suspense>
  );

  return (
    <LanguageContext.Provider value={{ t, language }}>
      <GuidedHighlightHoverTooltip theme={theme} />
      <div className={appClasses.join(' ')}>
        <div className="App-title">
          <div className="App-title-text">
            <h1 onClick={handleGoHome} style={{ cursor: 'pointer' }}>AIMIE</h1>
            <p className="App-subtitle">{t('app_subtitle')}</p>
          </div>
          <div className="title-actions">
            <img
              src={uoftLogoUrl}
              alt="University of Toronto logo"
              className="uoft-logo"
            />
          </div>
        </div>

        {showCustomBuilder ? (
          <Suspense fallback={null}>
            <CustomClientBuilder
              theme={theme}
              onClose={() => {
                setSelectedProfile(null);
                setCustomProfile(null);
                showHomeView();
              }}
              onStartSession={handleStartCustomSession}
            />
          </Suspense>
        ) : (
          <>
            {isSingleColumnViewport && (
              <div className={singleColumnPanelMenuClasses.join(' ')} ref={singleColumnMenuRef}>
                <button
                  type="button"
                  className={`single-column-panel-menu__trigger${singleColumnPanelMenuNeedsCue ? ' single-column-panel-menu__trigger--selection-cue' : ''}`}
                  data-guided-highlight-purpose={singleColumnPanelMenuNeedsCue ? 'Open the workspace menu to reach the suggested next action.' : undefined}
                  aria-label="Switch workspace panel"
                  aria-expanded={singleColumnMenuOpen}
                  onClick={() => setSingleColumnMenuOpen((open) => !open)}
                >
                  <span className="single-column-panel-menu__line single-column-panel-menu__line--top" />
                  <span className="single-column-panel-menu__line single-column-panel-menu__line--bottom" />
                </button>
                <ModeSelector
                  modeBarVisible
                  setModeBarVisible={() => {}}
                  activeMode={activePracticeMode}
                  onFreeForm={handleGoHome}
                  onBeTheClient={handleActAsClient}
                  onOars={handleEnterOars}
                  onStrategies={handleEnterStrategies}
                  variant="compact"
                />
                {activePracticeMode === 'freeform' && chatPanelHasHeader && (
                  <CumulativeTopicsPopover
                    t={t}
                    clientDescription={fullSessionVignette?.patient_description}
                    revealedItems={revealedItems}
                    open={topicsPanelOpen}
                    unreadCount={unreadTopicCount}
                    onToggle={handleTopicsPanelToggle}
                    placement="mobile"
                  />
                )}
                {(singleColumnMenuOpen || singleColumnDrawerMounted) && (
                  <div
                    className={`single-column-panel-menu__drawer${singleColumnMenuOpen ? '' : ' single-column-panel-menu__drawer--closing'}`}
                    role="dialog"
                    aria-modal="false"
                    aria-label="Workspace panel menu"
                  >
                    <button
                      type="button"
                      className="single-column-panel-menu__brand"
                      onClick={() => {
                        setSingleColumnMenuOpen(false);
                        handleGoHome();
                      }}
                    >
                      AIMIE
                    </button>
                    <button
                      type="button"
                      className="single-column-panel-menu__close"
                      aria-label="Close workspace panel menu"
                      onClick={() => setSingleColumnMenuOpen(false)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                    <div className="single-column-panel-menu__drawer-body">
                      <ClientSidebar
                        {...drawerClientSidebarProps}
                        collapsed={false}
                        onToggleCollapse={undefined}
                        hideHeader
                        className="client-sidebar--single-column-drawer"
                      />
                    </div>
                    {compactTurnNavigatorStatus && (
                      <ReportTurnNavigator
                        t={t}
                        totalTurns={compactTurnNavigatorStatus.totalTurns}
                        effectiveTurnIndex={compactTurnNavigatorStatus.effectiveTurnIndex}
                        isViewingLatest={compactTurnNavigatorStatus.isViewingLatest}
                        showStreamingTurn={compactTurnNavigatorStatus.showStreamingTurn}
                        onPrevTurn={compactTurnNavigatorStatus.onPrevTurn}
                        onNextTurn={compactTurnNavigatorStatus.onNextTurn}
                        className="single-column-panel-menu__turn-navigator"
                      />
                    )}
                    <div className="single-column-panel-menu__account-footer">
                      <div className="single-column-panel-menu__account-footer-row">
                        <ReportHamburgerMenu
                          t={t}
                          menuOpen={drawerAccountMenuOpen}
                          toggleMenu={() => setDrawerAccountMenuOpen((open) => !open)}
                          closeMenu={() => setDrawerAccountMenuOpen(false)}
                          menuRef={drawerAccountMenuRef}
                          menuPortalRef={drawerAccountMenuPortalRef}
                          handleThemeChange={(nextTheme) => {
                            handleThemeChange(nextTheme);
                            setDrawerAccountMenuOpen(false);
                          }}
                          theme={theme}
                          onShowContacts={() => { closeSingleColumnDrawer(); handleOpenOverlay('contacts'); }}
                          onShowAccount={() => { closeSingleColumnDrawer(); handleOpenAccountOverlay(); }}
                          onShowAbout={() => { closeSingleColumnDrawer(); handleOpenOverlay('about'); }}
                          onShowWorkingAlliance={() => { closeSingleColumnDrawer(); handleOpenOverlay('working-alliance'); }}
                          onShowLectures={() => { closeSingleColumnDrawer(); handleOpenOverlay('lectures'); }}
                          onShowTutorial={() => { closeSingleColumnDrawer(); handleOpenOverlay('tutorial'); }}
                          onShowAutomatedMiscMiti={() => { closeSingleColumnDrawer(); handleOpenOverlay('automated-misc-miti'); }}
                          onShowSimpleReflections={() => { closeSingleColumnDrawer(); handleOpenOverlay('simple-reflections'); }}
                          onShowAffirmations={() => { closeSingleColumnDrawer(); handleOpenOverlay('affirmations'); }}
                          onShowComplexReflections={() => { closeSingleColumnDrawer(); handleOpenOverlay('complex-reflections'); }}
                          onShowClient={() => { closeSingleColumnDrawer(); handleOpenOverlay('client'); }}
                          onShowCounsellors={(overlayId = null) => {
                            closeSingleColumnDrawer();
                            if (overlayId && !isDomEventLike(overlayId)) {
                              handleOpenOverlay(overlayId);
                              return;
                            }
                            handleOpenCounsellorOverlay(
                              selectedProfile === -2 ? clientModeCounsellorMode : sessionCounsellorMode
                            );
                          }}
                          guidedHighlightsEnabled={guidedHighlightsEnabled}
                          onGuidedHighlightsChange={setGuidedHighlightsEnabled}
                          skillsAnnotationsEnabled={skillsAnnotationsEnabled}
                          onSkillsAnnotationsChange={setSkillsAnnotationsEnabled}
                          suggestionsEnabled={suggestionsEnabled}
                          onSuggestionsChange={setSuggestionsEnabled}
                          language={language}
                          onLanguageChange={setLanguage}
                          authenticated={authenticated}
                          onSignOut={() => { closeSingleColumnDrawer(); handleSignOut(); }}
                          onSignIn={() => { closeSingleColumnDrawer(); requireAuth(); }}
                          triggerVariant="custom"
                          triggerClassName="single-column-account-trigger"
                          triggerAriaLabel="Open account menu"
                          menuPlacement="above"
                          submenuDirection="right"
                          hideAboutMenu
                          hideContactsMenuItem
                          compactAccountMenu
                          triggerContent={(
                            <>
                              <span className="single-column-account-trigger__avatar" aria-hidden="true">
                                <UserAvatarIcon
                                  avatarId={userAvatarPreference.avatarId}
                                  avatarBackground={userAvatarPreference.avatarBackground}
                                />
                              </span>
                              <span className="single-column-account-trigger__name">{drawerAccountName}</span>
                            </>
                          )}
                        />
                        <CreditsBadge authenticated={authenticated} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className={layoutClasses.join(' ')}>
        {/* Left sidebar - Client profiles */}
        <ClientSidebar
          {...clientSidebarProps}
          collapsed={shellSidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapsed}
        />

        {/* Center - Chat interface (handles all modes including OARS) */}
        <ChatInterface
          theme={theme}
          key={chatKey}
          profileIdx={selectedProfile}
          historicalReplay={historicalFullSessionReplay}
          customProfile={customProfile}
          sessionActive={sessionActive}
          onSessionEnd={handleSessionEnd}
          onSessionRegistered={(nextSessionId, options = {}) => {
            setSessionId(nextSessionId);
            setSessionReportMode(options.mode || null);
            setSessionCounsellorMode(options.counsellorMode || null);
            if (Object.prototype.hasOwnProperty.call(options, 'clientVignette')) {
              setFullSessionVignette(options.clientVignette);
            }
          }}
          onConversationStart={() => setConversationStarted(true)}
          onSessionReset={handleRestartSession}
          onSystemMessagesUpdate={handleSystemMessagesUpdate}
          onTurnClassification={handleFullSessionClassification}
          onTurnGuidance={handleFullSessionGuidance}
          onTurnGuidanceError={handleFullSessionGuidanceError}
          onRevealedItemsUpdate={handleRevealedItemsUpdate}
          onStageMetricsUpdate={handleStageMetricsUpdate}
          showNotification={showNotification}
          showConfirm={showConfirm}
          viewedTurnIndex={viewedTurnIndex}
          highlightedTurnIndex={highlightedTurnIndex}
          totalTurns={systemMessagesHistory.length}
          fullSessionAnnotations={visibleFullSessionAnnotations}
          fullSessionVignette={fullSessionVignette}
          revealedItems={revealedItems}
          topicsPanelOpen={topicsPanelOpen}
          unreadTopicCount={unreadTopicCount}
          onTopicsPanelToggle={handleTopicsPanelToggle}
          userFirstName={userFirstName}
          userAvatarPreference={userAvatarPreference}
          sessionCounsellorMode={sessionCounsellorMode}
          clientModeCounsellorMode={clientModeCounsellorMode}
          btcActivationRequest={btcActivationRequest}
          btcCueFlowActive={btcCueFlowActive}
          onBtcCueFlowActiveChange={setBtcCueFlowActive}
          activeMode={activePracticeMode}
          onFreeForm={handleGoHome}
          onBeTheClient={handleActAsClient}
          onOars={handleEnterOars}
          onStrategies={handleEnterStrategies}
          strategiesFlow={specificStrategiesFlow}
          oarsExercise={selectedProfile === -3 ? oarsExercise : undefined}
          oarsExerciseLoading={selectedProfile === -3 ? oarsExerciseLoading : undefined}
          oarsAttempt={selectedProfile === -3 ? oarsAttempt : null}
          isSpecificSkillReplay={Boolean(historicalSpecificSkillReplay)}
          oarsResetKey={selectedProfile === -3 ? oarsResetKey : undefined}
          onAttempt={selectedProfile === -3 ? handleOarsAttempt : undefined}
          onOarsAnalyzingChange={selectedProfile === -3 ? setOarsAnalyzing : undefined}
          onOarsStreamingEvaluationChange={selectedProfile === -3 ? setOarsStreamingEvaluation : undefined}
          oarsFilter={selectedProfile === -3 ? oarsFilter : undefined}
          oarsExerciseIndex={selectedProfile === -3 ? oarsExerciseIndex : undefined}
          oarsExerciseTotal={selectedProfile === -3 ? oarsExerciseTotal : undefined}
          specificSkillsAttemptCount={selectedProfile === -3 ? specificSkillsAttemptCount : 0}
          specificSkillsRetryAttemptId={selectedProfile === -3 ? specificSkillsRetryAttemptId : null}
          disableFullSessionCues={!guidedHighlightsEnabled}
          guidedHighlightsEnabled={guidedHighlightsEnabled}
          skillsAnnotationsEnabled={skillsAnnotationsEnabled}
          onOarsFilterChange={selectedProfile === -3 ? handleFreshOarsFilterChange : undefined}
          onOarsPopupBlockingChange={selectedProfile === -3 ? setOarsPopupBlocking : undefined}
          oarsInfoOpen={selectedProfile === -3 ? oarsInfoOpen : undefined}
          oarsInfoTab={selectedProfile === -3 ? oarsInfoTab : undefined}
          onOarsInfoOpen={selectedProfile === -3 ? handleOarsInfoOpen : undefined}
          onOarsInfoClose={selectedProfile === -3 ? () => setOarsInfoOpen(false) : undefined}
          oarsInfoShowCheckbox={selectedProfile === -3 ? oarsInfoShowCheckbox : undefined}
          onOarsInfoShowCheckboxChange={selectedProfile === -3 ? setOarsInfoShowCheckbox : undefined}
          dismissSkillPopups={selectedProfile === -3 ? oarsDismissSkillPopups : undefined}
          onDismissSkillPopupsChange={selectedProfile === -3 ? setOarsDismissSkillPopups : undefined}
          onLoadingChange={(active) => { setStreamingActive(active); if (active) setViewedTurnIndex(null); }}
          compactModeNavigator={compactModeNavigator}
          compactSessionControls={compactSessionControls}
          hidePracticeModeNavigator={isSingleColumnViewport}
          suppressAnnotationTooltips={isSingleColumnViewport && singleColumnMenuOpen}
        />

        {/* Right sidebar - Report/metrics */}
        <ReportPanel
          sessionId={sessionId}
          sessionActive={sessionActive}
          conversationStarted={conversationStarted}
          fullSessionVignette={fullSessionVignette}
          historicalReplay={historicalFullSessionReplay}
          clientModeCounsellorMode={clientModeCounsellorMode}
          systemMessagesHistory={systemMessagesHistory}
          viewedTurnIndex={viewedTurnIndex}
          onViewedTurnChange={setViewedTurnIndex}
          onHighlightedTurnChange={setHighlightedTurnIndex}
          revealedItems={revealedItems}
          stageMetrics={stageMetrics}
          onRestartSession={handleReportRestartSession}
          onFeedbackPdfDownloaded={() => {
            setPostDownloadNextStepCue(true);
            setFeedbackPdfDownloaded(true);
          }}
          onFeedbackReportReadyChange={setCompletedFullSessionViewActive}
          onCompactPostSessionStatusChange={setCompactPostSessionStatus}
          onCompactSessionObserverStatusChange={setCompactSessionObserverStatus}
          onCompactTurnNavigatorStatusChange={setCompactTurnNavigatorStatus}
          onHistoricalDownloadPdf={handleDownloadHistoricalReport}
          onHistoricalDownloadCsv={handleDownloadHistoricalTranscript}
          openDownloadsInNewTab={isSingleColumnViewport}
          onReturnToAccount={handleReturnToAccountFromReplay}
          showPostDownloadNextStepCue={
            guidedHighlightsEnabled && (
              !!historicalFullSessionReplay ||
              postDownloadNextStepCue ||
              (selectedProfile === -2 && btcPostSessionNextStepCue)
            )
          }
          disableFullSessionCues={!guidedHighlightsEnabled}
          suppressTransientNotices={!suggestionsEnabled || activeOverlay === 'account'}
          isClientMode={selectedProfile === -2}
          isOarsMode={selectedProfile === -3}
          isStrategiesMode={selectedProfile === -4}
          strategiesFlow={specificStrategiesFlow}
          isSpecificStrategyReplay={Boolean(historicalSpecificStrategyReplay)}
          onReturnToAccountFromSpecificStrategyReplay={handleReturnToAccountFromSpecificStrategyReplay}
          onSpecificStrategyTryAgain={handleSpecificStrategyTryAgain}
          isLlmModeSession={sessionReportMode === 'llm_mode'}
          sessionCounsellorMode={sessionCounsellorMode}
          oarsExercise={selectedProfile === -3 && !oarsEffectivelyBlocking ? oarsExercise : undefined}
          oarsAttempt={oarsAttempt}
          oarsAnalyzing={oarsAnalyzing}
          oarsStreamingEvaluation={oarsStreamingEvaluation}
          showSpecificSkillsPostAttemptCue={guidedHighlightsEnabled && selectedProfile === -3 ? !!oarsAttempt : false}
          oarsFilter={selectedProfile === -3 ? oarsFilter : undefined}
          onOarsTryAgain={historicalSpecificSkillReplay ? handleOarsReplayTryAgain : handleOarsTryAgain}
          onOarsNextExercise={handleOarsNextExercise}
          isSpecificSkillReplay={Boolean(historicalSpecificSkillReplay)}
          onReturnToAccountFromSpecificSkillReplay={handleReturnToAccountFromSpecificSkillReplay}
          guidedHighlightsEnabled={guidedHighlightsEnabled}
          onGuidedHighlightsChange={setGuidedHighlightsEnabled}
          skillsAnnotationsEnabled={skillsAnnotationsEnabled}
          onSkillsAnnotationsChange={setSkillsAnnotationsEnabled}
          suggestionsEnabled={suggestionsEnabled}
          onSuggestionsChange={setSuggestionsEnabled}
          preferredAnalyticsVisible={preferredAnalyticsVisible}
          onAnalyticsVisibleChange={setPreferredAnalyticsVisible}
          menuOpen={menuOpen}
          toggleMenu={toggleMenu}
          closeMenu={closeMenu}
          menuRef={menuRef}
          menuPortalRef={menuPortalRef}
          handleThemeChange={handleThemeChange}
          theme={theme}
          collapsed={shellInsightsCollapsed}
          onToggleCollapse={toggleInsightsCollapsed}
          onShowContacts={() => handleOpenOverlay('contacts')}
          onShowAccount={handleOpenAccountOverlay}
          onShowAbout={() => handleOpenOverlay('about')}
          onShowWorkingAlliance={() => handleOpenOverlay('working-alliance')}
          onShowLectures={() => handleOpenOverlay('lectures')}
          onShowTutorial={() => handleOpenOverlay('tutorial')}
          onShowAutomatedMiscMiti={() => handleOpenOverlay('automated-misc-miti')}
          onShowAutomatedMiscMitiCounsellor={() => handleOpenOverlay('automated-misc-miti', 'counsellor-miti-codes')}
          onShowSimpleReflections={() => handleOpenOverlay('simple-reflections')}
          onShowAffirmations={() => handleOpenOverlay('affirmations')}
          onShowComplexReflections={() => handleOpenOverlay('complex-reflections')}
          onShowClient={() => handleOpenOverlay('client')}
          onShowCounsellors={(overlayId = null) => {
            if (isDomEventLike(overlayId)) {
              handleOpenCounsellorOverlay(
                selectedProfile === -2 ? clientModeCounsellorMode : sessionCounsellorMode
              );
              return;
            }
            if (overlayId) {
              handleOpenOverlay(overlayId);
              return;
            }
            handleOpenCounsellorOverlay(
              selectedProfile === -2 ? clientModeCounsellorMode : sessionCounsellorMode
            );
          }}
          showNotification={showNotification}
          language={language}
          onLanguageChange={setLanguage}
          onSignOut={handleSignOut}
          streamingActive={streamingActive}
          isSingleColumnViewport={isSingleColumnViewport}
          onFullSessionGuidanceVisibleChange={setFullSessionGuidanceVisible}
        />
        <div className={`app-overlay-root ${theme}`}>
          {renderOverlayRouter()}
        </div>

            </div>
            <NotificationDialog
              notification={notification}
              closeNotification={closeNotification}
              t={t}
            />
            <ConfirmDialog
              confirmationDialog={confirmationDialog}
              handleConfirmResponse={handleConfirmResponse}
              t={t}
            />
            <Suspense fallback={null}>
              <FeedbackDialog
                feedbackDialogOpen={feedbackDialogOpen}
                feedbackContext={feedbackContext}
                feedbackLikertResponse={feedbackLikertResponse}
                setFeedbackLikertResponse={setFeedbackLikertResponse}
                feedbackSkillPracticeResponse={feedbackSkillPracticeResponse}
                setFeedbackSkillPracticeResponse={setFeedbackSkillPracticeResponse}
                feedbackResistanceResponse={feedbackResistanceResponse}
                setFeedbackResistanceResponse={setFeedbackResistanceResponse}
                feedbackWordCount={feedbackWordCount}
                feedbackText={feedbackText}
                setFeedbackText={setFeedbackText}
                countWords={countWords}
                feedbackSubmitting={feedbackSubmitting}
                handleFeedbackSubmit={handleFeedbackSubmit}
                closeFeedbackDialog={closeFeedbackDialog}
              />
            </Suspense>
          </>
        )}
      </div>
      <div className={`app-overlay-root ${theme}`}>
        <Suspense fallback={null}>
          <ExpandedSectionModal
            expandedSection={expandedSection}
            setExpandedSection={setExpandedSection}
            t={t}
            personaList={personaList}
            metadataEntries={metadataEntries}
            motivationList={motivationList}
            barrierList={barrierList}
            nextSteps={nextSteps}
            transcriptLines={transcriptLines}
          />
        </Suspense>
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
